// src/features/layout/PanelErrorBoundary.tsx
// A crashing rail panel must not take the app down with it: the boundary
// confines the failure to the panel body and offers a retry (remount).

import { Component, type ReactNode } from 'react';
import { useI18n } from '@react/hooks';

interface Props {
  /** Names the panel in the fallback so the error says what failed. */
  label: string;
  /** Changing the key remounts the panel — pass the active tab id. */
  resetKey?: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class PanelErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <PanelErrorFallback
          label={this.props.label}
          message={this.state.error.message}
          onRetry={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}

function PanelErrorFallback({
  label,
  message,
  onRetry,
}: {
  label: string;
  message: string;
  onRetry: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm">
      <p className="font-medium text-foreground">
        {t('layout.rail.panelError', { name: label })}
      </p>
      <p className="mt-1 break-words text-xs text-muted-foreground">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 rounded px-2 py-0.5 text-xs underline transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {t('layout.rail.retry')}
      </button>
    </div>
  );
}
